// Activity logging as a full page: search on top, "My activities" (every
// distinct thing the user ever logged — custom or catalog — reusable with one
// tap), the popular strip, then the whole catalog alphabetically as a plain
// scrolling list. Anything missing can be created as a CUSTOM activity: name,
// optional emoji, an intensity preset that maps to an honest MET value, and
// the same live calorie preview. Logging a custom once adds it to "My
// activities" forever (no extra schema — it derives from the log history).
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useUnits } from "../lib/useUnits";
import { todayLocal } from "../lib/date";
import { Button } from "../components/ui/Button";
import {
  ACTIVITY_CATALOG, POPULAR_ACTIVITIES, activityById, activityKcal,
  emojiOf, legacyTypeOf, searchActivities, type CatalogActivity,
} from "@shared/activity-catalog";

interface Pick {
  name: string;
  emoji: string;
  met: number;
  legacy: ReturnType<typeof legacyTypeOf>;
}

interface Mine { name: string; emoji: string | null; met: number }

/** Honest intensity presets for custom activities (Compendium bands). */
const INTENSITIES = [
  { key: "act.int_light", met: 2.5 },
  { key: "act.int_moderate", met: 4.5 },
  { key: "act.int_vigorous", met: 7.0 },
  { key: "act.int_max", met: 9.5 },
] as const;

export function ActivityLog() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const units = useUnits();
  const [params] = useSearchParams();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") ?? "") ? params.get("date")! : todayLocal();
  const weightKg = Number(params.get("w")) > 0 ? Number(params.get("w")) : 70;

  const [query, setQuery] = useState("");
  const [mine, setMine] = useState<Mine[]>([]);
  const [selected, setSelected] = useState<Pick | null>(null);
  const [custom, setCustom] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customEmoji, setCustomEmoji] = useState("");
  const [customMet, setCustomMet] = useState<number>(4.5);
  const [duration, setDuration] = useState("30");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const nameOf = (id: string) => t(`act.${id}`);

  useEffect(() => {
    let cancelled = false;
    api.myActivities()
      .then((m) => { if (!cancelled) setMine(m); })
      .catch(() => { /* section simply stays empty */ });
    return () => { cancelled = true; };
  }, []);

  const toPick = (a: CatalogActivity): Pick =>
    ({ name: nameOf(a.id), emoji: emojiOf(a), met: a.met, legacy: legacyTypeOf(a) });
  const minePick = (m: Mine): Pick =>
    ({ name: m.name, emoji: m.emoji || "🔥", met: m.met, legacy: "other" });

  const q = query.trim().toLowerCase();
  const catalogResults = useMemo(
    () => searchActivities(query, nameOf),
    [query, t], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const mineResults = q.length >= 2 ? mine.filter((m) => m.name.toLowerCase().includes(q)) : [];
  const all = useMemo(
    () => [...ACTIVITY_CATALOG].sort((a, b) => nameOf(a.id).localeCompare(nameOf(b.id))),
    [t], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const popular = useMemo(
    () => POPULAR_ACTIVITIES.map((id) => activityById(id)).filter((a): a is CatalogActivity => a !== null),
    [],
  );

  const minutes = Math.max(0, Math.round(Number(duration.replace(",", ".")) || 0));
  const minutesOk = minutes >= 1 && minutes <= 1440;
  const activeMet = custom ? customMet : selected?.met ?? null;
  const preview = activeMet !== null && minutesOk ? activityKcal(activeMet, weightKg, minutes) : null;
  const customOk = customName.trim().length >= 2;

  function openCustom() {
    setCustomName(query.trim());
    setCustomEmoji("");
    setCustomMet(4.5);
    setCustom(true);
  }

  async function add() {
    const pick: Pick | null = custom
      ? customOk
        ? { name: customName.trim().slice(0, 60), emoji: customEmoji.trim().slice(0, 8) || "🔥", met: customMet, legacy: "other" }
        : null
      : selected;
    if (!pick || !minutesOk || busy) return;
    setBusy(true);
    try {
      await api.addActivity({
        performed_on: date,
        performed_at: Math.floor(Date.now() / 1000),
        type: pick.legacy,
        duration_min: minutes,
        distance_km: null,
        kcal_burned: activityKcal(pick.met, weightKg, minutes),
        note: note.trim() || null,
        emoji: pick.emoji,
        met: pick.met,
        activity_name: pick.name,
      });
      toast.success(t("toast.logged"));
      navigate(-1);
    } catch {
      toast.error(t("toast.failed"));
      setBusy(false);
    }
  }

  const Row = ({ emoji, name, met, onTap }: { emoji: string; name: string; met: number; onTap: () => void }) => (
    <button
      type="button"
      onClick={onTap}
      className="w-full flex items-center gap-3 px-1 py-3 border-b border-line press text-left"
    >
      <span aria-hidden="true" className="text-2xl leading-none w-8 text-center shrink-0">{emoji}</span>
      <span className="flex-1 text-sm font-medium truncate">{name}</span>
      <span className="text-sm text-fg-muted tabular-nums shrink-0">{met} MET</span>
    </button>
  );

  const CreateRow = () => (
    <button
      type="button"
      onClick={openCustom}
      className="w-full flex items-center gap-3 px-1 py-3 border-b border-line press text-left"
    >
      <span aria-hidden="true" className="text-2xl leading-none w-8 text-center shrink-0 text-brand">＋</span>
      <span className="flex-1 text-sm font-medium text-brand">
        {q.length >= 2 ? t("act.create_named", { name: query.trim() }) : t("act.custom_create")}
      </span>
    </button>
  );

  const inDetail = custom || selected !== null;

  return (
    <div className="h-screen-d bg-canvas text-fg flex flex-col overflow-hidden">
      <header className="flex items-center gap-2 px-5 pt-safe pt-4 pb-2 shrink-0">
        <button
          onClick={() => (inDetail ? (setSelected(null), setCustom(false)) : navigate(-1))}
          aria-label={t("common.back")}
          className="text-fg-muted text-2xl leading-none -ml-1 press min-w-[44px] min-h-[44px] text-left"
        >
          ‹
        </button>
        <h1 className="text-base font-semibold truncate">
          {custom ? t("act.custom_title") : selected ? selected.name : t("act.title")}
        </h1>
      </header>

      {!inDetail ? (
        // min-h-0 is what makes a flex child actually scrollable.
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-24">
          <input
            className="input mb-4"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("act.search_ph")}
            inputMode="search"
          />
          {q.length >= 2 ? (
            <>
              {mineResults.length > 0 && (
                <>
                  <div className="text-sm text-fg-muted uppercase tracking-wider mb-1">{t("act.mine")}</div>
                  {mineResults.map((m) => (
                    <Row key={`m-${m.name}-${m.met}`} emoji={m.emoji || "🔥"} name={m.name} met={m.met} onTap={() => setSelected(minePick(m))} />
                  ))}
                  <div className="mb-3" />
                </>
              )}
              {catalogResults.map((a) => (
                <Row key={a.id} emoji={emojiOf(a)} name={nameOf(a.id)} met={a.met} onTap={() => setSelected(toPick(a))} />
              ))}
              <CreateRow />
              {catalogResults.length === 0 && mineResults.length === 0 && (
                <p className="text-sm text-fg-muted text-center py-6">{t("act.no_results")}</p>
              )}
            </>
          ) : (
            <>
              {mine.length > 0 && (
                <>
                  <div className="text-sm text-fg-muted uppercase tracking-wider mb-1">{t("act.mine")}</div>
                  {mine.slice(0, 6).map((m) => (
                    <Row key={`m-${m.name}-${m.met}`} emoji={m.emoji || "🔥"} name={m.name} met={m.met} onTap={() => setSelected(minePick(m))} />
                  ))}
                  <div className="mb-4" />
                </>
              )}
              <div className="text-sm text-fg-muted uppercase tracking-wider mb-2">{t("act.popular")}</div>
              <div className="flex flex-wrap gap-2 mb-5">
                {popular.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelected(toPick(a))}
                    className="px-3 py-2 rounded-full bg-surface text-sm font-medium press flex items-center gap-1.5"
                  >
                    <span aria-hidden="true">{emojiOf(a)}</span> {nameOf(a.id)}
                  </button>
                ))}
              </div>
              <div className="text-sm text-fg-muted uppercase tracking-wider mb-1">{t("act.all")}</div>
              <CreateRow />
              {all.map((a) => (
                <Row key={a.id} emoji={emojiOf(a)} name={nameOf(a.id)} met={a.met} onTap={() => setSelected(toPick(a))} />
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-24">
          {custom ? (
            <>
              <label className="block mb-3">
                <span className="text-sm text-fg-muted uppercase tracking-wider">{t("act.custom_name")}</span>
                <input
                  className="input mt-1"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  maxLength={60}
                  autoFocus
                />
              </label>
              <label className="block mb-3">
                <span className="text-sm text-fg-muted uppercase tracking-wider">{t("act.custom_emoji")}</span>
                <input
                  className="input mt-1 text-center text-xl"
                  value={customEmoji}
                  onChange={(e) => setCustomEmoji(e.target.value)}
                  placeholder="🔥"
                  maxLength={8}
                />
              </label>
              <div className="mb-3">
                <span className="text-sm text-fg-muted uppercase tracking-wider">{t("act.custom_intensity")}</span>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {INTENSITIES.map(({ key, met }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCustomMet(met)}
                      className={`py-3 px-3 rounded-xl text-sm font-medium transition press ${
                        customMet === met ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"
                      }`}
                    >
                      {t(key)} · {met} MET
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center mb-5 mt-2">
              <div aria-hidden="true" className="text-6xl mb-3">{selected!.emoji}</div>
              <span className="inline-block text-sm text-fg-muted bg-surface-strong rounded-full px-3 py-1 tabular-nums">
                {selected!.met} MET
              </span>
            </div>
          )}

          <label className="block mb-3">
            <span className="text-sm text-fg-muted uppercase tracking-wider">{t("act.duration")}</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                className="input flex-1 text-center text-lg tabular-nums"
                type="text"
                inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
              <span className="text-sm text-fg-muted w-10">{t("act.minutes")}</span>
            </div>
          </label>

          <label className="block mb-4">
            <span className="text-sm text-fg-muted uppercase tracking-wider">{t("act.note_label")}</span>
            <input
              className="input mt-1"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("act.note_ph")}
              maxLength={120}
            />
          </label>

          <div className="rounded-2xl bg-surface p-4 text-center mb-4">
            <div className="text-3xl font-bold tabular-nums text-accent-move">
              {preview !== null ? `≈ ${units.energy(preview).value}` : "—"}
              <span className="text-base font-medium text-fg-muted"> {units.energy(0).unit}</span>
            </div>
          </div>

          <Button fullWidth onClick={add} disabled={!minutesOk || busy || (custom && !customOk)}>
            {busy ? t("common.loading") : t("act.add")}
          </Button>
        </div>
      )}
    </div>
  );
}
